import { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

type PictureData = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

interface BeforeAfterSliderProps {
  beforeImage: string | PictureData;
  afterImage: string | PictureData;
  beforeLabel?: string;
  afterLabel?: string;
}

const renderPicture = (
  source: string | PictureData,
  alt: string,
  className: string,
  style?: React.CSSProperties,
  loading: "eager" | "lazy" = "lazy"
) => {
  if (typeof source === "string") {
    return <img src={source} alt={alt} className={className} style={style} draggable={false} loading={loading} decoding="async" />;
  }
  return (
    <picture>
      {Object.entries(source.sources).map(([type, srcset]) => (
        <source key={type} srcSet={srcset} type={type} sizes="(max-width: 768px) 100vw, 800px" />
      ))}
      <img
        src={source.img.src}
        width={source.img.w}
        height={source.img.h}
        alt={alt}
        className={className}
        style={style}
        draggable={false}
        loading={loading}
        decoding="async"
      />
    </picture>
  );
};

const BeforeAfterSlider = ({
  beforeImage,
  afterImage,
  beforeLabel = "Before",
  afterLabel = "After",
}: BeforeAfterSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPosition((x / rect.width) * 100);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) updatePosition(e.clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    updatePosition(e.touches[0].clientX);
  };

  return (
    <motion.div
      ref={containerRef}
      className="relative w-full aspect-square md:aspect-[16/10] rounded-2xl overflow-hidden cursor-col-resize select-none border border-border/30 gold-glow-strong"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      initial={{ opacity: 0, scale: 0.98 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* After image (bottom layer — full width) */}
      {renderPicture(afterImage, "After detailing", "absolute inset-0 w-full h-full object-cover object-center")}

      {/* Before image (top layer — clipped by slider) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        {renderPicture(
          beforeImage,
          "Before detailing",
          "absolute inset-0 h-full object-cover object-center",
          { width: containerWidth ? `${containerWidth}px` : "100%", maxWidth: "none" }
        )}
      </div>

      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className="w-[2px] h-full bg-gradient-to-b from-primary/40 via-primary/80 to-primary/40" />
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full gold-gradient-bg flex items-center justify-center shadow-xl gold-glow-strong border-2 border-primary-foreground/20"
          whileHover={{ scale: 1.2 }}
          animate={{ 
            boxShadow: [
              "0 0 20px hsl(43 72% 52% / 0.2)", 
              "0 0 40px hsl(43 72% 52% / 0.4)", 
              "0 0 20px hsl(43 72% 52% / 0.2)"
            ] 
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 4L3 12L8 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary-foreground" />
            <path d="M16 4L21 12L16 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary-foreground" />
          </svg>
        </motion.div>
      </div>

      {/* Labels */}
      <motion.span 
        className="absolute top-5 left-5 text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-xl bg-background/80 text-foreground backdrop-blur-md border border-border/30"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        {beforeLabel}
      </motion.span>
      <motion.span 
        className="absolute top-5 right-5 text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-xl gold-gradient-bg text-primary-foreground shadow-lg"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        {afterLabel}
      </motion.span>

      {/* Drag hint */}
      <motion.div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-foreground/60 tracking-widest uppercase glass-premium rounded-full px-4 py-1.5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 1, duration: 4, times: [0, 0.1, 0.8, 1] }}
      >
        ← Drag to Compare →
      </motion.div>
    </motion.div>
  );
};

export default BeforeAfterSlider;
